import { useEffect } from "react"
import { Flex, Tag, Typography } from "antd"
import { ClockCircleOutlined, FileTextOutlined, GlobalOutlined, UserOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import { getExperienceOptions } from "store/trips"
import { TRIP_LANGUAGES } from "constants/experiences"
import { formatDurationFields, formatExperienceTerms } from "utils/experience"
import { useTranslation } from "react-i18next"
import styles from "./PreviewAboutV2.module.css"

const { Title, Paragraph, Text } = Typography

// Fallback for the hardcoded language values; real languages are dynamic from the backend.
const langLabelFallback = Object.fromEntries((TRIP_LANGUAGES || []).map((language) => [language.value, language.label]))

const getCategoryName = (categories) => {
  const list = Array.isArray(categories) ? categories : categories ? [categories] : []
  const first = list[0]
  if (!first) return null
  return typeof first === "object" ? first.name : first
}

// Booking-option durations (minutes) → a friendly "2-4 hours" range.
const formatDuration = (tripPrices) => {
  const durations = (tripPrices || []).map((price) => price.trip_duration).filter((value) => value != null)
  if (!durations.length) return null
  const toHours = (minutes) => {
    const hours = minutes / 60
    return Number.isInteger(hours) ? `${hours}` : hours.toFixed(1)
  }
  const min = Math.min(...durations)
  const max = Math.max(...durations)
  return min === max ? `${toHours(min)} hours` : `${toHours(min)}-${toHours(max)} hours`
}

/**
 * v2 preview "About" section: category tag, key meta rows (group size, duration,
 * languages, license) and the description. Driven by the server trip.
 */
export const PreviewAboutV2 = ({ trip }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const experienceOptions = useSelector((state) => state.trips.experienceOptions?.data)

  // trip.languages stores language IDs; resolve them to names via the dynamic
  // backend options list. Fetch it if a direct preview hasn't loaded it yet.
  useEffect(() => {
    if (!experienceOptions?.languages?.length) dispatch(getExperienceOptions())
  }, [dispatch, experienceOptions])

  const languageNameById = new Map((experienceOptions?.languages || []).map((language) => [language.id, language.name]))
  const resolveLanguage = (language) => {
    if (language && typeof language === "object") return language.name
    return languageNameById.get(language) ?? langLabelFallback[language] ?? language
  }

  // The guide's listing carries its category as a `category` object; `categories` is the list page's shape.
  const category = getCategoryName(trip?.categories ?? trip?.category)
  const maxGuests = trip?.max_guests
  // Multi-day → day count; single-day → duration_value/unit direct fields,
  // falling back to the range derived from trip_prices if the direct fields are absent.
  const duration =
    trip?.duration_type === "multi_day"
      ? formatExperienceTerms(trip, t)
      : formatDurationFields(trip, t) ?? formatDuration(trip?.trip_prices)
  const languagesText = (trip?.languages || []).map(resolveLanguage).join(" & ")
  const licensesList = trip?.licenses_list
  const licenseName = Array.isArray(licensesList) && licensesList.length ? licensesList.join(", ") : "License"
  const licenseText = trip?.licenses_required ? `${licenseName} required` : null

  return (
    <div className={styles.container}>
      <Title level={3} className={styles.title}>{"About"}</Title>

      {category && <Tag className={styles.categoryTag}>{category}</Tag>}

      <Flex vertical gap={10} className={styles.metaList}>
        {maxGuests != null && (
          <Flex align="center" gap={10}>
            <UserOutlined className={styles.metaIcon} />
            <Text className={styles.metaText}>{`Max of ${maxGuests} per group`}</Text>
          </Flex>
        )}
        {duration && (
          <Flex align="center" gap={10}>
            <ClockCircleOutlined className={styles.metaIcon} />
            <Text className={styles.metaText}>{duration}</Text>
          </Flex>
        )}
        {languagesText && (
          <Flex align="center" gap={10}>
            <GlobalOutlined className={styles.metaIcon} />
            <Text className={styles.metaText}>{languagesText}</Text>
          </Flex>
        )}
        {licenseText && (
          <Flex align="center" gap={10}>
            <FileTextOutlined className={styles.metaIcon} />
            <Text className={styles.metaText}>{licenseText}</Text>
          </Flex>
        )}
      </Flex>

      {trip?.description && <Paragraph className={styles.description}>{trip.description}</Paragraph>}
    </div>
  )
}

PreviewAboutV2.propTypes = {
  trip: PropTypes.shape({
    categories: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.string]),
    max_guests: PropTypes.number,
    languages: PropTypes.arrayOf(PropTypes.string),
    licenses_required: PropTypes.bool,
    licenses_list: PropTypes.array,
    trip_prices: PropTypes.array,
    description: PropTypes.string,
    duration_type: PropTypes.string,
    duration_value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    duration_unit: PropTypes.string
  })
}
